import { OverlayContainer } from "@angular/cdk/overlay";
import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import {
  MatSelect,
  MatOption,
  MatOptgroup,
  MatSelectChange,
  MatSelectTrigger,
} from "@angular/material/select";
import { RouterLink, Router } from "@angular/router";
import { IconComponent } from "@claas/claas-ui-components";
import { TranslatePipe } from "@ngx-translate/core";
import { combineLatest, map, of, switchMap, finalize } from "rxjs";
import { CustomOverlayContainer } from "../../../core/providers/custom-overlay-container";
import { pathRouteParametersHelper$ } from "../../helpers/path-params.helper";
import { DefaultCompetitor } from "../../pia-api/generated";
import { PiaCompetitorsService } from "../../pia-api/pia-competitors.service";
import { CompetitorsGroupedByBrand } from "../../resolvers/competitors.resolver";

@Component({
  selector: "claas-competitor-selection-dialog",
  imports: [
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatOptgroup,
    TranslatePipe,
    IconComponent,
    RouterLink,
    MatSelectTrigger,
  ],
  templateUrl: "./competitor-selection-dialog.html",
  styleUrl: "./competitor-selection-dialog.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: OverlayContainer,
      useFactory: () => new CustomOverlayContainer("[data-main-modal-overlay]"),
    },
  ],
})
export class CompetitorSelectionDialog {
  competitorsService = inject(PiaCompetitorsService);
  router = inject(Router);

  displayArgumentsOrTechData = computed(() =>
    this.router.url.includes("arguments") ? "arguments" : "tech-data",
  );

  productID = toSignal(
    pathRouteParametersHelper$().pipe(map((params) => params["id"] || "")),
    {
      initialValue: "",
    },
  );

  countryID = toSignal(
    pathRouteParametersHelper$().pipe(map((params) => params["country"] || "")),
    {
      initialValue: "",
    },
  );

  languageID = toSignal(
    pathRouteParametersHelper$().pipe(
      map((params) => params["language"] || ""),
    ),
    {
      initialValue: "",
    },
  );

  year = toSignal(
    pathRouteParametersHelper$().pipe(map((params) => params["year"] || "")),
    {
      initialValue: "",
    },
  );

  productDetailID = toSignal(
    pathRouteParametersHelper$().pipe(
      map((params) => params["productId"] || ""),
    ),
    {
      initialValue: "",
    },
  );

  userSelectedProductDetailID = input<string>("");

  computedProductDetailID = computed(() =>
    this.userSelectedProductDetailID()
      ? this.userSelectedProductDetailID()
      : this.productDetailID(),
  );

  isLoading = signal(false);

  data = toSignal(
    combineLatest([
      toObservable(this.computedProductDetailID),
      pathRouteParametersHelper$(),
    ]).pipe(
      switchMap(([productDetailID, params]) => {
        const countryID = params["country"]?.toUpperCase();
        const languageID = params["language"];
        const year = params["year"];
        if (productDetailID && countryID && languageID && year) {
          this.isLoading.set(true);
          return this.competitorsService
            .getComparableCompetitorsGroupedByBrand({
              productID: productDetailID,
              countryID,
              languageID,
              year,
            })
            .pipe(finalize(() => this.isLoading.set(false)));
        }
        this.isLoading.set(false);
        return of(null);
      }),
    ),
    { initialValue: null },
  );

  selectedCompetitorProduct = signal<DefaultCompetitor | null>(null);
  hasSelection = computed(() => this.selectedCompetitorProduct() !== null);

  brandsArray = computed(() => {
    const dataValue: CompetitorsGroupedByBrand | null = this.data();
    const showBrandPrefix = this.hasSelection();

    if (!dataValue) return [];

    return Object.entries(dataValue).map(([key, value]) => ({
      key,
      value: {
        ...value,
        items: value.items.map(
          (item: DefaultCompetitor & { benchmarkIconId?: string }) => ({
            ...item,
            displayName: showBrandPrefix
              ? `${value.brand}: ${item.Name}`
              : item.Name,
          }),
        ),
      },
    }));
  });

  competitorDisplayText = computed(() => {
    const currentCompetitor: DefaultCompetitor | null =
      this.selectedCompetitorProduct();
    if (!currentCompetitor) return "";

    const dataValue = this.data();
    if (!dataValue) return "";

    for (const value of Object.values(dataValue)) {
      if (value.items.some((item) => item.ID === currentCompetitor.ID)) {
        return `${value.brand} ${currentCompetitor?.Name}`;
      }
    }
    return "";
  });

  @Output() closeCompetitorsDialog = new EventEmitter<string | null>();

  protected readonly computed = computed;

  handleCloseCompetitorsDialogClick(evt: MouseEvent, disabled: boolean) {
    if (disabled) {
      evt.preventDefault();
      return;
    }
    this.closeCompetitorsDialog.emit(this.selectedCompetitorProduct()?.ID);
  }

  handleCompetitorProductSelectionChange(evt: MatSelectChange) {
    this.selectedCompetitorProduct.set(evt.value);
  }
}
